import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { configureApiClient } from '@eatout/api-client'

import { AppProviders } from '@/app/providers'
import { queryClient } from '@/app/query-client'
import { AppRouter } from '@/app/router'
import { themeSchema } from '@/features/settings/schemas/theme'

import '@/styles/globals.css'

function isDarkThemePreference(): boolean {
  try {
    const stored = window.localStorage.getItem('eatout-ui')
    if (stored === null) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }

    const parsed: unknown = JSON.parse(stored)
    if (typeof parsed !== 'object' || parsed === null || !('state' in parsed)) {
      return false
    }

    const state = Reflect.get(parsed, 'state')
    if (typeof state !== 'object' || state === null) {
      return false
    }

    return Reflect.get(state, 'theme') === 'dark'
  } catch {
    return false
  }
}

if (isDarkThemePreference()) {
  document.documentElement.classList.add('dark')
}

configureApiClient({
  baseURL: import.meta.env.VITE_API_URL,
  onUnauthorized: () => {
    queryClient.clear()
    const { pathname, search } = window.location
    if (pathname === '/login' || pathname === '/register') {
      return
    }

    const redirect = encodeURIComponent(`${pathname}${search}`)
    window.location.assign(`/login?redirect=${redirect}`)
  },
})

themeSchema.parse(document.documentElement.dataset.theme ?? 'light')

const rootElement = document.querySelector('#root')

if (!rootElement) {
  throw new Error('Root element #root not found')
}

createRoot(rootElement).render(
  <StrictMode>
    <AppProviders>
      <AppRouter />
    </AppProviders>
  </StrictMode>,
)

const { initSentry } = await import('@/app/sentry')
initSentry()
